import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert, AuthCard } from '@sentinel/ui';
import { isSafeReturnTo } from '@sentinel/auth-core';

// One-shot read of the bridge-handoff slot. The /login bridge consumer stashes
// the MFA challenge token here so it never needs to appear in our SPA URL.
// `consume*` clears the slot so a reload / back-button can't replay it.
function consumeMfaTokenFromSessionStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem('mfa_token');
    if (v) window.sessionStorage.removeItem('mfa_token');
    return v;
  } catch {
    return null;
  }
}

function consumeMfaReturnToFromSessionStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem('mfa_return_to');
    if (v) window.sessionStorage.removeItem('mfa_return_to');
    return v;
  } catch {
    return null;
  }
}
import {
  ChallengeMethodPicker,
  type ChallengeMethod,
} from '../features/mfa/components/challenge-method-picker';
import { TotpChallengeStep } from '../features/mfa/components/totp-challenge-step';
import { WebAuthnChallengeStep } from '../features/mfa/components/webauthn-challenge-step';
import { RecoveryChallengeStep } from '../features/mfa/components/recovery-challenge-step';
import {
  useTotpVerify,
  useRecoveryVerify,
  useWebauthnVerify,
} from '../features/mfa/use-challenge';

const searchSchema = z.object({
  token: z.string().optional(),
  return_to: z.string().optional(),
});

interface ErrorBody {
  error?: string;
  message?: string;
}
function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}
function getBody(e: unknown): ErrorBody | undefined {
  if (e && typeof e === 'object') {
    const maybe = e as { body?: ErrorBody } & ErrorBody;
    return maybe.body ?? maybe;
  }
  return undefined;
}

// Backend (sentinel-app) returns DomainError::Unauthorized with the literal
// message "invalid or expired MFA challenge token" when the mfa_token is bad
// or expired. There's no structured error code today, so we match that exact
// phrase rather than substring-matching on "token"/"expired" (which would
// mis-route messages like "Invalid TOTP code" or "id token mismatch"). When
// the backend gains a stable `error: "mfa_token_expired"` code, prefer that.
function isInvalidMfaToken(status: number | undefined, body: ErrorBody | undefined) {
  if (status !== 401) return false;
  const text = `${body?.error ?? ''} ${body?.message ?? ''}`.toLowerCase();
  return text.includes('invalid or expired mfa');
}

// Backend mfa_webauthn_start returns DomainError::Validation
// "no WebAuthn MFA credentials registered" (HTTP 400) when the user has
// not enrolled any WebAuthn second factor.
function isNoWebAuthnFactor(body: ErrorBody | undefined) {
  const text = `${body?.error ?? ''} ${body?.message ?? ''}`.toLowerCase();
  return text.includes('no webauthn');
}

function MfaVerifyPage() {
  const { t } = useTranslation('mfa');
  const search = Route.useSearch();
  // Prefer the URL query param (preserves backward compatibility with internal
  // links / deep-links that still pass `?token=...`). Fall back to the
  // sessionStorage handoff that /login fills from the bridge cookie when the
  // user came via the magic-link → MFA path. useMemo so a re-render doesn't
  // re-consume the (now-cleared) slot.
  const mfaToken = useMemo(
    () => search.token ?? consumeMfaTokenFromSessionStorage(),
    [search.token],
  );
  const sessionReturnTo = useMemo(
    () => (search.return_to ? null : consumeMfaReturnToFromSessionStorage()),
    [search.return_to],
  );
  const returnTo = useMemo(() => {
    const candidate = search.return_to ?? sessionReturnTo ?? undefined;
    return isSafeReturnTo(candidate) ? candidate : '/launch';
  }, [search.return_to, sessionReturnTo]);

  const [method, setMethod] = useState<ChallengeMethod>('totp');
  const [topError, setTopError] = useState<string | null>(null);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [webauthnError, setWebauthnError] = useState<string | null>(null);
  const [webauthnStatus, setWebauthnStatus] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle');

  const totpVerify = useTotpVerify(mfaToken);
  const recoveryVerify = useRecoveryVerify(mfaToken);
  const webauthnVerify = useWebauthnVerify(mfaToken);

  const supportsWebAuthn =
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.get === 'function';

  function switchMethod(next: ChallengeMethod) {
    if (next === method) return;
    setMethod(next);
    // Reset transient state so a stale error / pending UI doesn't bleed across.
    setTopError(null);
    setTotpError(null);
    setRecoveryError(null);
    setWebauthnError(null);
    setWebauthnStatus('idle');
    totpVerify.reset();
    recoveryVerify.reset();
    webauthnVerify.reset();
  }

  function navigateOnSuccess() {
    window.location.assign(returnTo);
  }

  async function onTotpSubmit(code: string) {
    setTotpError(null);
    setTopError(null);
    try {
      await totpVerify.mutateAsync({ code });
      navigateOnSuccess();
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      if (isInvalidMfaToken(status, body)) {
        setTopError(t('expired_token_message'));
        return;
      }
      if (status === 401 || status === 400) {
        setTotpError(t('verify_invalid_code'));
        return;
      }
      setTopError(t('verify_generic_error'));
    }
  }

  async function onRecoverySubmit(recoveryCode: string) {
    setRecoveryError(null);
    setTopError(null);
    try {
      await recoveryVerify.mutateAsync({ recovery_code: recoveryCode });
      navigateOnSuccess();
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      if (isInvalidMfaToken(status, body)) {
        setTopError(t('expired_token_message'));
        return;
      }
      if (status === 401 || status === 400) {
        setRecoveryError(t('verify_invalid_recovery_code'));
        return;
      }
      setTopError(t('verify_generic_error'));
    }
  }

  async function onWebauthnStart() {
    setWebauthnError(null);
    setTopError(null);
    setWebauthnStatus('pending');
    try {
      await webauthnVerify.mutateAsync();
      setWebauthnStatus('success');
      navigateOnSuccess();
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      setWebauthnStatus('error');
      if (isInvalidMfaToken(status, body)) {
        setTopError(t('expired_token_message'));
        return;
      }
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        setWebauthnError(t('webauthn_failed'));
        return;
      }
      if (status === 400 && isNoWebAuthnFactor(body)) {
        setWebauthnError(t('verify_no_webauthn_factor'));
        return;
      }
      setWebauthnError(t('webauthn_failed'));
    }
  }

  // Without an mfa_token the page is non-functional. Render an error state
  // with a link back to /login. (The token is single-use and short-lived;
  // reload is the most common reason to land here.)
  if (!mfaToken) {
    return (
      <AuthCard title={t('invalid_token_title')}>
        <Alert type="error" role="alert">
          {t('invalid_token_message')}
        </Alert>
        <p>
          <Link to="/login">{t('return_to_login')}</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('verify_title')} subtitle={t('verify_subtitle')}>
      <ChallengeMethodPicker active={method} onChange={switchMethod} />

        {topError ? (
          <Alert type="error" role="alert">
            {topError}
          </Alert>
        ) : null}

        {method === 'totp' && (
          <TotpChallengeStep
            onSubmit={onTotpSubmit}
            error={totpError}
            submitting={totpVerify.isPending}
          />
        )}

        {method === 'webauthn' && (
          <WebAuthnChallengeStep
            onStart={onWebauthnStart}
            status={webauthnStatus}
            error={webauthnError}
            supported={supportsWebAuthn}
          />
        )}

      {method === 'recovery' && (
        <RecoveryChallengeStep
          onSubmit={onRecoverySubmit}
          error={recoveryError}
          submitting={recoveryVerify.isPending}
          lostDeviceHref={(() => {
            const params = new URLSearchParams({ token: mfaToken });
            if (search.return_to) params.set('return_to', search.return_to);
            return `/mfa/recovery?${params.toString()}`;
          })()}
        />
      )}
    </AuthCard>
  );
}

export const Route = createFileRoute('/mfa/verify')({
  validateSearch: searchSchema,
  component: MfaVerifyPage,
});
